import React, {useContext, useEffect, useState} from "react";
import {Container, Typography} from "@material-ui/core";
import {useDate, useI18N} from "../../../common/common";
import PersonalDetailField from "../../widget/profile/PersonalDetailField";
import {EDITABLE_FIELD, GET_SALUTATION, SALUTATIONS} from "../../../common/constant";
import {useUserAccounts} from "../../../hooks/useUserAccounts";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {COUNTRY_CODES} from "../../../common/country-codes";
import {useKeycloakFunctions} from "../../../hooks/useKeycloakFunctions";
import {getPostCodeRegex} from "../../../common/country-postcodes";
import PropTypes from "prop-types";
import {withSnackbar} from "../../hoc/SnackbarHOC";
import {getDateLabel} from "../../../selectors/dates";
import {CustomButton, CountryIcon} from "@hapinest/theme";

const {makeStyles} = require("@material-ui/core/styles");

const useStyles = makeStyles((theme) => ({
    countryIcon: {
        width: theme.spacing(2),
        height: theme.spacing(2),
        marginRight: theme.spacing(1)
    },
    mt5: {
        marginTop: theme.spacing(5)
    },
    mb3: {
        marginBottom: theme.spacing(3)
    },
    textBold: {
        fontWeight: "600"
    },
    snackbarContent: {
        [theme.breakpoints.up("sm")]: {
            width: "80vw"
        },
        [theme.breakpoints.up("md")]: {
            width: "60vw"
        },
        [theme.breakpoints.up("lg")]: {
            width: "40vw"
        }
    },
}));

const maxLengthRestrictions = {
    first_name: 39,
    last_name: 39,
    email: 240,
    dial_code: 4,
    phone_number: 16,
    street: 29,
    house_number: 9,
    postal_code: 10,
    city: 34,
};

const PersonalDetailsView = ({showSnackbar}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const [localeState] = useContext(LocaleContext);
    const {getUser, updateUser, loadingUpdateUser, user, updatedUser, errorUpdateUser} = useUserAccounts();
    const {isProspect, isTrialMember, updateEmail} = useKeycloakFunctions();
    const {formatDate} = useDate();
    const [viewLoading, setViewLoading] = useState(true);
    const [formOnEditMode, setFormOnEditMode] = useState(false);
    const [languageDropdown, setLanguageDropdown] = useState([]);
    const [languageNames, setLanguageNames] = useState({});
    const [countryCodesDropdown, setCountryCodesDropdown] = useState([]);
    const [countries, setCountries] = useState();

    const snackbar = {
        type: "success",
        withIcon: true,
        className: classes.snackbarContent
    };
    const getParentOfField = (field) => {
        const fields = {
            salutation: "salutation",
            first_name: "name",
            last_name: "name",
            email: "email",
            phone_number_country_code: "phone",
            phone_number: "phone",
            street: "address",
            postal_code: "address",
            city: "address",
            country: "address",
            language: "language",
        };

        return fields[field];
    };

    const handleUpdateEmail = () => {
        if (updateEmail) {
            updateEmail();
        }
    };

    const getInitialFields = () => {
        return {
            member_id: {
                title: i18n("view.marketplace.admin.manage_offers.member"),
                display_value: "",
                editable: EDITABLE_FIELD.FALSE,
                remoteResponse: undefined,
                fields: {}
            },
            salutation: {
                title: i18n("view.profile.account_settings.salutation"),
                display_value: "",
                editable: EDITABLE_FIELD.TRUE,
                remoteResponse: undefined,
                fields: {
                    salutation: {
                        type: "radio",
                        label: i18n("view.profile.account_settings.salutation"),
                        value: "",
                        helperText: "",
                        validationRules: [],
                        enabled: true,
                        options: SALUTATIONS,
                        columns: {
                            xs: 12
                        }
                    }
                }
            },
            name: {
                title: i18n("view.profile.account_settings.name"),
                display_value: "",
                editable: (isTrialMember() || isProspect()) ? EDITABLE_FIELD.TRUE : EDITABLE_FIELD.DISABLED,
                remoteResponse: undefined,
                fields: {
                    first_name: {
                        type: "text",
                        label: i18n("view.profile.account_settings.name"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {type: "required", message: i18n("common.form.validation_errors.enter_first_name")},
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.first_name,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.first_name})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 6
                        },
                        transformationRule: "capitalize"
                    },
                    last_name: {
                        type: "text",
                        label: i18n("view.profile.account_settings.surname"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {type: "required", message: i18n("common.form.validation_errors.enter_last_name")},
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.last_name,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.last_name})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 6
                        },
                        transformationRule: "capitalize"
                    }
                }
            },
            date: {
                title: i18n("view.profile.account_settings.date"),
                display_value: "",
                editable: EDITABLE_FIELD.DISABLED,
                remoteResponse: undefined,
                fields: {}
            },
            email: {
                title: i18n("view.auth.input.username"),
                display_value: "",
                editable: EDITABLE_FIELD.FALSE,
                remoteResponse: undefined,
                actionButton: (
                    <CustomButton
                        size={"small"}
                        disableRipple
                        onClick={handleUpdateEmail}
                    >
                        {i18n("common.label.update")}
                    </CustomButton>
                )
            },
            phone: {
                title: i18n("view.profile.account_settings.phone_number"),
                display_value: "",
                editable: EDITABLE_FIELD.TRUE,
                remoteResponse: undefined,
                fields: {
                    phone_number_country_code: {
                        type: "select",
                        label: i18n("view.profile.account_settings.country"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.dial_code,
                                message: i18n("common.form.validation_errors.max_digits", {maxLength: maxLengthRestrictions.dial_code})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 4,
                            sm: 2
                        }
                    },
                    phone_number: {
                        type: "text",
                        label: i18n("view.profile.account_settings.phone_number"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {type: "required", message: i18n("common.form.validation_errors.enter_phone")},
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.phone_number,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.phone_number})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 8,
                            sm: 10
                        }
                    }
                }
            },
            address: {
                title: i18n("view.profile.account_settings.address"),
                display_value: "",
                editable: EDITABLE_FIELD.TRUE,
                remoteResponse: undefined,
                fields: {
                    street: {
                        type: "text",
                        label: i18n("view.profile.account_settings.street"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.street,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.street})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 9,
                            sm: 10
                        },
                        transformationRule: "capitalize"
                    },
                    house_number: {
                        type: "text",
                        label: i18n("view.profile.account_settings.house_number"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.house_number,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.house_number})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 3,
                            sm: 2,
                        }
                    },
                    postal_code: {
                        type: "text",
                        label: i18n("view.profile.account_settings.postal_code"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {type: "required", message: i18n("common.form.validation_errors.enter_postcode")},
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.postal_code,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.postal_code})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 5,
                            sm: 3
                        }
                    },
                    city: {
                        type: "text",
                        label: i18n("view.profile.account_settings.town"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [
                            {type: "required", message: i18n("common.form.validation_errors.enter_city")},
                            {
                                type: "maxLength",
                                value: maxLengthRestrictions.city,
                                message: i18n("common.form.validation_errors.max_characters", {maxLength: maxLengthRestrictions.city})
                            },
                        ],
                        enabled: true,
                        columns: {
                            xs: 7,
                            sm: 9
                        },
                        transformationRule: "capitalize"
                    },
                    country: {
                        type: "text",
                        label: i18n("view.profile.account_settings.country"),
                        value: "",
                        helperText: "Country can not be changed",
                        errorText: "",
                        error: false,
                        validationRules: [],
                        enabled: false,
                        columns: {
                            xs: 12
                        }
                    }
                }
            },
            language: {
                title: i18n("view.profile.account_settings.language_of_correspondence"),
                display_value: "",
                editable: EDITABLE_FIELD.TRUE,
                remoteResponse: undefined,
                fields: {
                    language: {
                        type: "select",
                        label: i18n("view.profile.account_settings.language"),
                        value: "",
                        helperText: "",
                        errorText: "",
                        error: false,
                        validationRules: [],
                        enabled: true,
                        options: languageDropdown,
                        columns: {
                            xs: 12
                        }
                    }
                }
            },
        };
    };

    const [fields, setFields] = useState(getInitialFields());

    const saveEnabledFieldsAndUpdateUser = (name, localFields) => {
        const newFields = {...fields};
        Object.keys(localFields).forEach((key2) => {
            if (localFields[key2].enabled) {
                newFields[name].fields[key2].value = localFields[key2].value;
            }
        });
        setFields(newFields);
        sendUpdateUserRequestForEnabledFields(localFields);
    };

    const sendUpdateUserRequestForEnabledFields = (localFields) => {
        const updateBody = {};
        Object.keys(localFields).forEach((key) => {
            if (localFields[key].enabled) {
                updateBody[key] = localFields[key].value;
            }
        });
        updateUser(updateBody);
    };

    const handleOpenSnackbar = (type, message) => {
        showSnackbar({
            ...snackbar,
            type: type,
            message: message,
        });
    };

    /**
     * Get User.
     */
    useEffect(() => {
        getUser();
    }, []);

    /**
     * Update fields that depend on locale context.
     */
    useEffect(() => {
        if (localeState?.localeSelection?.localeOptions && localeState.localeUppercaseUnderscore) {
            const languages = {};
            const languagesArray = [];
            const countryCodes = [];

            localeState?.localeSelection?.localeOptions.forEach((item) => {
                let itemKey = item.value.split("_")[0];
                if (!["at_DE", "ch_DE"].includes(item.value)) {
                    languages[itemKey] = {
                        label: item.label,
                        key: item.key,
                        value: itemKey
                    };
                }
            });
            Object.keys(languages).forEach((key) => {
                languagesArray.push(languages[key]);
            });

            COUNTRY_CODES.forEach((key) => {
                countryCodes.push({
                    label: <div key={key.isoCode}>
                        <CountryIcon
                            countryCode={key.isoCode}
                            className={classes.countryIcon}
                        />

                        <span>{key.dialCode}</span>
                    </div>,
                    key: key.name,
                    value: key.dialCode,
                });
            });

            setLanguageNames(languages);
            setLanguageDropdown(languagesArray);
            setCountryCodesDropdown(countryCodes);

            const locale = localeState.localeUppercaseUnderscore.indexOf("en") === 0 ? "en" : localeState.localeUppercaseUnderscore;
            import("../../../common/i18n/countries/country_" + locale).then(res => {
                setCountries(res?.COUNTRY);
            });
        }
    }, [localeState]);

    /**
     * Populate fields on user change.
     */
    useEffect(() => {
        if (user && countries) {
            const fieldsObject = {
                ...fields
            };

            fieldsObject.member_id.display_value = user?.member_id;

            fieldsObject.salutation.display_value = i18n(GET_SALUTATION[user?.salutation]?.label);
            fieldsObject.salutation.fields.salutation.value = user?.salutation;

            fieldsObject.name.display_value = [user?.first_name, user?.last_name].join(" ");
            fieldsObject.name.fields.first_name.value = user?.first_name;
            fieldsObject.name.fields.last_name.value = user?.last_name;

            fieldsObject.date.display_value = user?.date_of_birth ? getDateLabel(user.date_of_birth, "", formatDate, {day: "numeric", month: "numeric", year: "numeric"}) : "";

            fieldsObject.email.display_value = user?.email;

            fieldsObject.phone.display_value = user?.phone_number_country_code && user?.phone_number ? "(" + user?.phone_number_country_code + ") " + user?.phone_number : "";
            fieldsObject.phone.fields.phone_number_country_code.value = user?.phone_number_country_code;
            fieldsObject.phone.fields.phone_number.value = user?.phone_number;

            const countryIsoCode = user?.country?.toUpperCase();
            const country = countries?.[countryIsoCode];
            fieldsObject.address.display_value = (
                <>
                    <span>{[user?.street, user?.house_number].join(" ")}</span>
                    <br/>
                    <span>{[user?.postal_code, user?.city].join(" ")}</span>
                    <br/>
                    <span>{country}</span>
                </>
            );
            fieldsObject.address.fields.street.value = (user?.street ? user?.street : "");
            fieldsObject.address.fields.house_number.value = (user?.house_number ? user?.house_number : "");
            fieldsObject.address.fields.postal_code.value = user?.postal_code;
            fieldsObject.address.fields.postal_code.validationRules.push({
                type: "pattern",
                value: getPostCodeRegex(countryIsoCode),
                message: i18n("common.form.validation_errors.wrong_format_postcode")
            });
            fieldsObject.address.fields.city.value = user?.city;
            fieldsObject.address.fields.country.value = country;

            fieldsObject.language.display_value = languageNames[user?.language]?.label;
            fieldsObject.language.fields.language.value = user?.language;

            setFields(fieldsObject);
            setViewLoading(false);
        }
    }, [user, countries]);

    /**
     * Populate fields on dropdown change.
     */
    useEffect(() => {
        if (languageDropdown && languageDropdown.length > 0) {
            const fieldsObject = {
                ...fields
            };

            fieldsObject.language.fields.language.options = languageDropdown;

            setFields(fieldsObject);
            setViewLoading(false);
        }
    }, [languageDropdown]);

    /**
     * Populate fields on country code change.
     */
    useEffect(() => {
        if (countryCodesDropdown && countryCodesDropdown.length > 0) {
            const fieldsObject = {
                ...fields
            };

            fieldsObject.phone.fields.phone_number_country_code.options = countryCodesDropdown;

            setFields(fieldsObject);
            setViewLoading(false);
        }
    }, [countryCodesDropdown]);

    useEffect(() => {
        if (updatedUser) {
            let parentKey = "";
            Object.keys(updatedUser).some((key) => {
                let parent = getParentOfField(key);
                if (parent) {
                    parentKey = parent;
                    return true;
                }
                return false;
            });

            const newFields = {...fields};
            newFields[parentKey]["remoteResponse"] = {
                success: true,
                requestTime: Date.now()
            };
            setFields(newFields);

            handleOpenSnackbar("success2",
                <Typography variant="body1">
                    <>
                        <span
                            className={classes.textBold}
                        >{i18n("common.label.success")}</span>{" " + i18n("view.profile.account_settings.save_success_message")}
                    </>
                </Typography>
            );
        }
    }, [updatedUser]);

    useEffect(() => {
        if (errorUpdateUser) {
            if (errorUpdateUser?.status === 422) {
                let parentKey = getParentOfField(errorUpdateUser?.data.detail[0].loc[1]);

                const newFields = {...fields};
                newFields[parentKey]["remoteResponse"] = {
                    success: false,
                    requestTime: Date.now(),
                    fieldErrors: {}
                };
                errorUpdateUser?.data.detail?.forEach((item) => {
                    newFields[parentKey]["remoteResponse"]["fieldErrors"][item.loc[1]] = item.msg;
                });
                setFields(newFields);
            } else {
                if (errorUpdateUser?.status === 409 && errorUpdateUser?.data?.detail === "email_already_exists") {
                    handleOpenSnackbar("warning2",
                        <Typography variant="body1">
                            <>
                                <span
                                    className={classes.textBold}
                                >{i18n("common.form.validation_errors.email_already_exists")}</span>{" " + i18n("common.form.validation_errors.please_use_another_email")}
                            </>
                        </Typography>
                    );
                } else {
                    handleOpenSnackbar("warning2",
                        <Typography variant="body1">
                            <>
                                <span
                                    className={classes.textBold}
                                >{i18n("view.profile.account_settings.save_error_message")}</span>{" " + i18n("common.label.please_try_again")}
                            </>
                        </Typography>
                    );
                }
            }
        }
    }, [errorUpdateUser]);

    return (
        <>
            <Container
                maxWidth={"md"}
                className={classes.mt5}
            >
                <Typography
                    variant={"h3"}
                    className={classes.mb3}
                >
                    {i18n("view.profile.account_settings.personal_details")}
                </Typography>

                {!viewLoading && Object.keys(fields).map((key) => (
                    <PersonalDetailField
                        key={key}
                        name={key}
                        title={fields[key].title}
                        display_value={fields[key].display_value}
                        editable={fields[key].editable}
                        fields={fields[key].fields}
                        remoteResponse={fields[key].remoteResponse}
                        onFieldSave={saveEnabledFieldsAndUpdateUser}
                        formOnEditMode={formOnEditMode}
                        setFormOnEditMode={setFormOnEditMode}
                        loading={loadingUpdateUser}
                        actionButton={fields[key].actionButton}
                    />
                ))}
            </Container>
        </>
    );
};

PersonalDetailsView.propTypes = {
    showSnackbar: PropTypes.func
};
export default withSnackbar(PersonalDetailsView);
